import { Route } from '@angular/router';
import { HomeComponent } from '@cnt-workspace/home';
import { authGuard, editOwnerGuard, poiOwnerGuard, reservationProcessingGuard, salesAccessGuard } from '@cnt-workspace/data-access';
import { buildActivityCategoryRoutes } from '@cnt-workspace/activity-location';

export const appRoutes: Route[] = [
  { path: '', component: HomeComponent },
    {
    path: 'auth/login',
    loadComponent: () => import('@cnt-workspace/auth').then(m => m.SignInComponent),
  },
  {
    path: 'auth/register',
    loadComponent: () => import('@cnt-workspace/auth').then(m => m.SignUpComponent),
  },
  {
    path: 'auth/confirm',
    loadComponent: () => import('@cnt-workspace/auth').then(m => m.AuthConfirmComponent),
  },
  {
    path: 'auth/redirect',
    loadComponent: () => import('@cnt-workspace/auth').then(m => m.AuthRedirectComponent),
  },
  {
    path: 'auth/recover',
    loadComponent: () => import('@cnt-workspace/auth').then(m => m.ForgotPasswordComponent),
  },
  {
    path: 'auth/reset-password',
    loadComponent: () => import('@cnt-workspace/auth').then(m => m.ResetPasswordComponent),
  },
  {
    path: 'auth/new-password',
    loadComponent: () => import('@cnt-workspace/auth').then(m => m.ForceNewPasswordComponent),
  },
  {
    path: 'auth/temp-login',
    loadComponent: () => import('@cnt-workspace/auth').then(m => m.TempLoginComponent),
  },
  {
    path: 'welcome',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/auth').then(m => m.RoleSelectorComponent),
  },
  {
    path: 'welcome/source',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/auth').then(m => m.SourceSelectorComponent),
  },
  {
    path: 'welcome/guest',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/auth').then(m => m.GuestOnboardingComponent),
  },
  {
    path: 'booking/review',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/booking').then(m => m.BookingReviewComponent),
  },
  {
    path: 'booking/confirm/:id',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/booking').then(m => m.BookingConfirmComponent),
  },
  {
    // Legacy URL baked into every reservation email/push (server
    // generateReservationLink). The guard always redirects to the guest's or
    // host's page for that reservation, so this route never renders.
    path: 'reservation-processing/:id',
    canActivate: [reservationProcessingGuard],
    children: [],
  },
  {
    path: 'user-dashboard/trips',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/trips').then(m => m.TripsComponent),
  },
  {
    path: 'trip-planner',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/trip-planner').then(m => m.TripPlannerListComponent),
  },
  {
    path: 'trip-planner/:id',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/trip-planner').then(m => m.TripPlannerEditComponent),
  },
  {
    path: 'trip/share',
    loadComponent: () => import('@cnt-workspace/trip-planner').then(m => m.TripShareViewerComponent),
  },
  {
    path: 'user-dashboard/message',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/inbox').then(m => m.InboxComponent),
  },
  {
    path: 'user-dashboard/message/:threadId',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/inbox').then(m => m.InboxComponent),
  },
  {
    path: 'user-dashboard/dashboard',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/dashboard').then(m => m.DashboardComponent),
  },
  {
    path: 'hosting',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/host-dashboard').then(m => m.HostDashboardComponent),
  },
  {
    path: 'hosting/calendar',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/host-dashboard').then(m => m.HostBulkCalendarComponent),
  },
  {
    // Deliberately unlinked from any nav — reachable only via the footnote link on the
    // Account → Payouts tab (see payouts.component.html).
    path: 'hosting/tax-settings',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/host-tax-settings').then(m => m.HostTaxSettingsComponent),
  },
  {
    // P101/B — Un-gated so anonymous visitors can walk the wizard.
    // Publish action inside Phase3ReviewComponent enforces auth via a
    // "sign in to publish" prompt; the draft persists to
    // localStorage[cnt-listing-draft] and resumes after signup.
    path: 'properties/new',
    loadComponent: () => import('@cnt-workspace/hosting-new-listing').then(m => m.HostingNewListingComponent),
  },
  {
    path: 'properties',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/hosting-listings').then(m => m.HostingListingsComponent),
  },
  {
    path: 'properties/:id/calendar',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/hosting-listings').then(m => m.HostListingCalendarComponent),
  },
  {
    path: 'properties/edit/:id',
    canActivate: [authGuard, editOwnerGuard],
    loadComponent: () => import('@cnt-workspace/hosting-new-listing').then(m => m.HostingNewListingComponent),
  },
  {
    path: 'properties/:id/addons',
    canActivate: [authGuard, editOwnerGuard],
    loadComponent: () => import('@cnt-workspace/hosting-new-listing').then(m => m.HostingAddonsStandaloneComponent),
  },
  {
    path: 'properties/:id/sites',
    canActivate: [authGuard, editOwnerGuard],
    loadComponent: () => import('@cnt-workspace/hosting-new-listing').then(m => m.HostingSitesStandaloneComponent),
  },
  {
    path: 'hosting/addons',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/hosting-new-listing').then(m => m.BulkAddonsBuilderComponent),
  },
  {
    path: 'user-dashboard/user-profile',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/account').then(m => m.AccountComponent),
  },
  {
    path: 'user-profile/verify-address-change/:id',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/account').then(m => m.VerifyAddressChangeComponent),
  },
  {
    path: 'wishlists',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/wishlists').then(m => m.WishlistsComponent),
  },
  {
    path: 'friend',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/refer').then(m => m.ReferComponent),
  },
  {
    path: 'sales/clients',
    canActivate: [authGuard, salesAccessGuard],
    loadComponent: () => import('@cnt-workspace/sales-clients').then(m => m.SalesClientsComponent),
  },
  { path: 'host/dashboard', redirectTo: 'hosting', pathMatch: 'full' },
  { path: 'host-more', redirectTo: 'host', pathMatch: 'full' },
  { path: 'guest-more', redirectTo: '', pathMatch: 'full' },
  {
    path: 'search',
    loadComponent: () =>
      import('@cnt-workspace/search').then(m => m.SearchResultsComponent),
  },
  {
    path: 'rv-camping/:location/:name/:listingId',
    loadComponent: () =>
      import('@cnt-workspace/listing').then(m => m.ListingDetailsComponent),
  },
  {
    path: 'rv-camping/:name/:listingId',
    loadComponent: () =>
      import('@cnt-workspace/listing').then(m => m.ListingDetailsComponent),
  },
  {
    path: 'rv-camping/:listingId',
    loadComponent: () =>
      import('@cnt-workspace/listing').then(m => m.ListingDetailsComponent),
  },
  {
    // Deprecated alias — old cnt-landing links were query-param based
    // (/listing?id=123), not path-segment based like legacy's, so this
    // forwards straight to the bare-id fallback route rather than porting
    // legacy's path-segment forwarder guard.
    path: 'listing',
    redirectTo: ({ queryParams }) => `/rv-camping/${queryParams['id'] ?? ''}`,
  },
  {
    path: 'boondocking/:location/:name/:boondockId',
    loadComponent: () =>
      import('@cnt-workspace/listing').then(m => m.BoondockDetailsComponent),
  },
  {
    path: 'boondocking/:name/:boondockId',
    loadComponent: () =>
      import('@cnt-workspace/listing').then(m => m.BoondockDetailsComponent),
  },
  {
    path: 'boondocking/:boondockId',
    loadComponent: () =>
      import('@cnt-workspace/listing').then(m => m.BoondockDetailsComponent),
  },
  {
    path: 'activity/add',
    canActivate: [authGuard],
    loadComponent: () => import('@cnt-workspace/activity-location').then(m => m.ActivityFormComponent),
  },
  {
    path: 'activity/edit/:id',
    canActivate: [authGuard, poiOwnerGuard],
    loadComponent: () => import('@cnt-workspace/activity-location').then(m => m.ActivityFormComponent),
  },
  ...buildActivityCategoryRoutes(),
  {
    path: 'host',
    loadComponent: () => import('@cnt-workspace/host-space').then(m => m.HostSpaceComponent),
  },
  {
    path: 'hh',
    loadComponent: () => import('@cnt-workspace/host-space').then(m => m.HostPersonaLandingComponent),
    data: { personaKey: 'hh' },
  },
  {
    path: 'farm',
    loadComponent: () => import('@cnt-workspace/host-space').then(m => m.HostPersonaLandingComponent),
    data: { personaKey: 'farm' },
  },
  {
    path: 'land',
    loadComponent: () => import('@cnt-workspace/host-space').then(m => m.HostPersonaLandingComponent),
    data: { personaKey: 'land' },
  },
  {
    path: 'driveway',
    loadComponent: () => import('@cnt-workspace/host-space').then(m => m.HostPersonaLandingComponent),
    data: { personaKey: 'driveway' },
  },
  {
    path: 'winery',
    loadComponent: () => import('@cnt-workspace/host-space').then(m => m.HostPersonaLandingComponent),
    data: { personaKey: 'winery' },
  },
  {
    path: 'brewery',
    loadComponent: () => import('@cnt-workspace/host-space').then(m => m.HostPersonaLandingComponent),
    data: { personaKey: 'brewery' },
  },
  {
    path: 'attraction',
    loadComponent: () => import('@cnt-workspace/host-space').then(m => m.HostPersonaLandingComponent),
    data: { personaKey: 'attraction' },
  },
  {
    path: 'hobby',
    loadComponent: () => import('@cnt-workspace/host-space').then(m => m.HostPersonaLandingComponent),
    data: { personaKey: 'hobby' },
  },
  {
    path: 'checklist',
    loadComponent: () => import('@cnt-workspace/guest-checklist').then(m => m.GuestChecklistComponent),
  },
    {
    path: 'faq',
    loadComponent: () => import('@cnt-workspace/content').then(m => m.FaqComponent),
  },
  {
    path: 'help',
    loadComponent: () => import('@cnt-workspace/content').then(m => m.HelpCenterComponent),
  },
  {
    path: 'contact',
    loadComponent: () => import('@cnt-workspace/content').then(m => m.ContactComponent),
  },
  {
    path: 'terms/privacy',
    loadComponent: () => import('@cnt-workspace/content').then(m => m.PrivacyComponent),
  },
  {
    path: 'terms',
    loadComponent: () => import('@cnt-workspace/content').then(m => m.TermsComponent),
  },
  {
    path: 'cookies',
    loadComponent: () => import('@cnt-workspace/content').then(m => m.CookiesComponent),
  },
  {
    path: 'grow',
    loadComponent: () => import('@cnt-workspace/content').then(m => m.GrowComponent),
  },
  {
    path: 'host-resources',
    loadComponent: () => import('@cnt-workspace/content').then(m => m.HostResourcesComponent),
  },
  {
    path: 'article',
    loadComponent: () => import('@cnt-workspace/content').then(m => m.ArticlesComponent),
  },
  {
    path: 'article/saved',
    data: { mode: 'saved' },
    loadComponent: () => import('@cnt-workspace/content').then(m => m.ArticlesComponent),
  },
  {
    path: 'article/category/:key',
    data: { mode: 'category' },
    loadComponent: () => import('@cnt-workspace/content').then(m => m.ArticlesComponent),
  },
  {
    path: 'article/:id/:slug',
    loadComponent: () => import('@cnt-workspace/content').then(m => m.ArticleDetailComponent),
  },
  {
    path: 'article/:id',
    loadComponent: () => import('@cnt-workspace/content').then(m => m.ArticleDetailComponent),
  },
  {
    path: 'explore/:state',
    loadComponent: () => import('@cnt-workspace/explore').then(m => m.ExploreStateComponent),
  },
  {
    path: '**',
    loadComponent: () => import('@cnt-workspace/content').then(m => m.NotFoundComponent),
  },
];
